// Email and SMS the studio has asked for, as a queue.
//
// Nothing here sends anything. The places a teacher asks for a message are all
// over the product: a receipt when a payment is taken, cancellation notices
// when lessons are deleted, lesson notes to a parent.
//
// So the request is recorded, exactly the way invoice generation records one:
// the client appends a document and a Cloud Function consumes it
// (functions/delivery/deliver.js).
//
// Pure, like the rest of domain/: the same normalisation runs in the function
// that drains the queue.

// `.js`, not extensionless: functions/ is "type": "module", so the generated
// mirror of this file is loaded by Node's ESM resolver, which does not guess
// extensions. Vite builds either way; only the deployed function throws.
// `./core.js`: this file is copied into functions/domain/ too (#218).
import { coreSync } from './core.js';
import { documentFromRows, documentToRows } from './documents.js';

// ── Across the boundary ────────────────────────────────────────────────────
//
// The rules below — who a message goes to, the cancellation audience and its
// warning, the requests each surface queues, the document and the outbox's
// fold — are `core/src/messages.rs`, reached through core-ffi/src/messages.rs,
// so the Android and iOS apps address the same people and write the same
// documents. What is left here is the coercion of the loose objects the web
// holds into the core's records, the core's rows back into the request and
// document objects the web writes, `statedAudience`'s refusal (a guard on
// those loose objects), and `contactWantsReminder`, which only the server's
// reminder sweep reads.
//
// The vocabulary below stays as literals, because a Cloud Function reads it at
// module scope; test/domain/messagesCore.test.js holds it to the core's.

export const CHANNELS = { email: 'email', sms: 'sms' };

// What is being sent. The kind decides which template the sender renders and
// what `context` has to carry for it to render without the client's help — a
// receipt is a server-side artefact and must not depend on a browser being
// open, the same requirement the PDF reports have.
//
// `event_reminder` and `invoice_reminder` are queued by the scheduled reminder
// worker (functions/reminders/, decided by domain/reminders.js), not by a
// teacher, so their subjects are derived from the lesson or the invoice: the
// derived id is what keeps an hourly worker from sending a family twenty
// copies. MMS has both as System Email and SMS Templates (Event Reminder,
// Invoice Reminder).
//
// `teacher_message` is the email a teacher writes from Message History's New
// Message ▾ (#522): a subject line and a body of their own, the one kind whose
// words are not a template's. See "New Message" in core/src/messages.rs.
export const MESSAGE_KINDS = {
  payment_receipt: 'payment_receipt',
  lesson_notes: 'lesson_notes',
  event_cancelled: 'event_cancelled',
  event_reminder: 'event_reminder',
  invoice_reminder: 'invoice_reminder',
  teacher_message: 'teacher_message',
  // An invoice emailed with its PDF attached (#622): the Send Invoice
  // drawer's, or New Invoice's "Yes, email invoices immediately" — see
  // ./invoiceEmail.js and core/src/invoice_email.rs.
  invoice: 'invoice',
  // A Family Portal invitation (#770), queued by the server alone — see
  // functions/portal/invites.js and core/src/family_portal.rs.
  portal_invite: 'portal_invite',
  // A Family Portal booking's confirmation to the family (#773), a
  // cancellation's (#775) and a reschedule's (#543), each queued by the
  // server alone — see functions/portal/book.js, cancel.js and
  // reschedule.js. The teacher's notices of all three are the product's
  // teacher mail, not studio messages (functions/portal/teacherNotices.js).
  portal_booking: 'portal_booking',
  portal_cancellation: 'portal_cancellation',
  portal_reschedule: 'portal_reschedule',
};

// `sending` is the sender's claim on a message: it has picked this one up and
// an attempt is in flight, so a second invocation — a platform retry, a
// redelivered trigger — can tell "already being sent" from "never started".
//
// Only the sender ever writes it. firestore.rules lets a client update a
// message only while it says `queued`, so a client can re-address a message
// that is still waiting and cannot touch one the sender has taken.
export const MESSAGE_STATUS = {
  queued: 'queued',
  sending: 'sending',
  sent: 'sent',
  failed: 'failed',
};

// Waiting, from the teacher's point of view, including `sending`: counting
// only `queued` would make the pending count tick down and back up again if
// the send failed.
export function isPending(message) {
  return coreSync().isMessagePending(str(message?.status));
}

// `String(v)` of a present value; absent and null are nothing.
const str = (v) => (v == null ? null : String(v));

const listOf = (v) => (Array.isArray(v) ? v : [v]);

const toFamily = (family) => ({ id: str(family.id), lastName: str(family.lastName) });

const toContact = (contact, group = 0) => ({
  group,
  id: str(contact.id),
  firstName: str(contact.firstName),
  lastName: str(contact.lastName),
  email: str(contact.email),
  mobile: str(contact.mobile),
  mobileSmsCapable: Boolean(contact.mobileSmsCapable),
});

// A `null` student is a student with nothing on file. Who, and whose family:
// a student is not a message destination (#792), so an `email` or `phone` a
// student document written before still carries is not read.
const toStudent = (student) => ({
  id: str(student?.id),
  familyId: str(student?.familyId),
  firstName: str(student?.firstName),
  lastName: str(student?.lastName),
});

const toContacts = (contacts) => listOf(contacts).filter(Boolean).map((c) => toContact(c));

const toRecipient = (r) => ({ name: str(r.name), address: String(r.address), channel: String(r.channel) });

const fromRecipient = (r) => ({ name: r.name ?? null, address: r.address, channel: r.channel });

const toOccurrence = (o) => ({
  id: str(o.id), seriesId: str(o.seriesId), date: str(o.date), ruleDate: str(o.ruleDate),
});

// The picker's answer: per channel, whether the family is told on it.
const toChoice = (chosen) => ({
  emailOn: chosen?.email === true,
  smsOn: chosen?.sms === true,
  note: str(chosen?.note),
});

// A request object, as each surface spells one, from the core's
// reference and its document: `cc` on the attendance drawer's receipt, `note`
// on a cancellation notice, and neither anywhere else.
function requestOf(ref, rows, extras = []) {
  const doc = documentFromRows(rows, null);
  const request = { kind: ref.kind, channel: ref.channel, subject: ref.subject, to: doc.to };
  if (extras.includes('cc')) request.cc = doc.cc;
  if (extras.includes('note')) request.note = doc.note;
  request.context = doc.context;
  return request;
}

// A recipient is a name and an address, both resolved here rather than at send
// time. The address a teacher chose is the one that should be written to: a
// parent who changes their email next month did not agree to receive this
// receipt at the new one, and a queue that resolves late would send it there.
export function recipient(name, address, { channel = CHANNELS.email } = {}) {
  return fromOptional(coreSync().messageRecipient(str(name), str(address), String(channel)));
}

const fromOptional = (r) => (r ? fromRecipient(r) : null);

// How a person is addressed on a message ("Ada Okafor") is `full_name` in
// core/src/messages.rs: *not* under the studio's Naming Format, because it is
// the display name on an address. Whether MMS applies Parent Name Format to its
// own To: headers is not known.
//
// How a person is listed in the delete dialog's "no address on file" warning
// is `listed_name`: a list rendering, so it follows the studio's Parent Name
// Format ("Nguyen, Ava", as MMS lists it).
//
// The address a person can be reached at on a channel is `address_on`. The
// delete dialog's warning is that returning null, so "who do we write to" and
// "who does the warning name" cannot disagree. SMS is a capability, not just a
// number on file: as in MMS, a contact with a phone number not marked
// SMS-capable is listed with "no SMS capable phone number on file".

// The people a *family account's* messages can go to — which is everybody a
// message goes to. MMS addresses the account, not the child, on the
// account-facing surfaces (lesson notes, payment receipts), and addresses
// students individually on a cancellation and from New Message; here a
// student is not a message destination at all (#792), and every surface
// addresses the family's contacts.
//
// `contacts` are `familyContacts` records (#319); `familyBillingContact` in
// ./people.js alone decides which one is the billing contact.
//
// ── Every contact, and the invoice surface is the exception (#413) ─────────
//
// **Every family contact, BCC'ed into one message**, as MMS does: its
// composer says "Multiple recipients are automatically BCC'ed", and an
// event's own `Email` action pre-fills every family contact, flagged or not
// (and the student, which is where we differ, #792). `contacts` is a *list*, and a surface decides which
// contacts it hands in.
//
// Who takes which audience:
//
//   every contact   lesson notes (useAttendanceForm), cancellation notices
//                   (cancellationRecipients below, both routes), lesson
//                   reminders and overdue-invoice reminders (domain/reminders.js),
//                   payment receipts (AddTransactionDrawer)
//   one contact     the invoice itself, the `invoice` kind (#622): To is one
//                   contact with an address, opening on the billing contact
//                   (`invoiceEmailChoices` in ./invoiceEmail.js), and New
//                   Invoice's emails go to the same default. MMS's Send
//                   Invoice drawer is a single-select `To` defaulting to the
//                   last-created contact flagged `Set as Preferred Invoice
//                   Recipient` (#393, #405); that flag narrows this drawer and
//                   nothing else.
//
// The overdue-reminder sweep is unattended, and what MMS's own unattended sends
// address is not known; it takes the composer's audience.
//
// The teacher still picks where MMS lets them pick: the attendance drawer's
// receipt is a single-select ("No Email Receipt" plus one contact) and its
// lesson notes a checkbox per contact, each listing every contact.
//
// The per-contact `emailReminders` / `smsReminders` flags are not part of this
// (#416): MMS captions them as contact × category, so they filter *this list*
// in the lesson-reminder sweep (`contactWantsReminder` below).
//
// **A repeated address appears once.** Two contacts sharing one email are one
// inbox. That is ours, not MMS's. Deduped on the resolved address exactly as
// stored, with no case folding.
//
// ── The shape ───────────────────────────────────────────────────────────────
//
// `contacts` is a list, and a single contact is accepted as a list of one:
// "the one contact an invoice goes to" is a real audience. The argument itself is required: a caller that forgot
// it would queue a message to nobody, indistinguishable from a family with no
// email on file.
//
// **`familyRecipients(...).length === 0` means "nobody on this family can be
// reached on this channel"**, which is what its call sites read it as.
//
// The address on a channel is `address_on` in core/src/messages.rs: `email`,
// or for SMS the contact's `mobile` while `mobileSmsCapable` is ticked.
export function familyRecipients(family, contacts, { channel = CHANNELS.email } = {}) {
  if (!family) return [];
  return coreSync().familyRecipients(toFamily(family), toContacts(contacts), String(channel)).map(fromRecipient);
}

// The lesson notes' checkboxes: the family's contacts by email, then "Myself"
// — left off when the teacher's address is already a contact's. Two rows at one address would be two checkboxes ticking together and two
// recipients on one message. `notes_recipients` in core/src/messages.rs.
export function notesRecipients(family, contacts, myEmail) {
  return coreSync()
    .notesRecipients(family ? toFamily(family) : undefined, toContacts(contacts), str(myEmail))
    .map(fromRecipient);
}

// The longest cancellation message `firestore.rules` accepts, in UTF-16 units
// — the two message boxes' `maxLength`.
export const CANCELLATION_NOTE_MAX = 3999;

// Whether a family contact has asked to be sent *lesson reminders* on this
// channel — the per-contact half of MMS's contact x category rule (#416).
//
// MMS's caption on the contact drawer is the specification:
//
//   "This family contact will only receive reminders for categories with
//    reminders enabled. To manage them, go to Categories & Locations."
//
// So it is an AND of the contact's own tick and the category's reminder lead.
// The category half is domain/categories.js, read by the sweep in
// ./reminders.js; this function is the contact half (#416).
//
// **Per channel**: MMS asks the email and the SMS questions separately.
//
// **`=== true`, so absent is off.** Both flags default false in
// `normaliseFamilyContact`, matching MMS's unticked boxes, and a contact
// without the fields is off — the same reading `messaging.sendInvoiceSms`
// takes.
//
// **Lesson reminders only.** An invoice reminder has no category, so the
// invoice loop in ./reminders.js is gated on the studio-wide switch instead.
// Cancellation notices are not reminders either (#413).
//
// A null contact answers **false** rather than throwing: an unreachable family
// and a family that said no are different skip lines, and the caller tells
// them apart.
export function contactWantsReminder(contact, { channel = CHANNELS.email } = {}) {
  if (!contact) return false;
  if (channel === CHANNELS.sms) return contact.smsReminders === true;
  return contact.emailReminders === true;
}

// A `familyContacts` record in the shape `addressOn` reads.
//
// (`contact_recipient` in core/src/messages.rs.) **The mobile number and its
// own tick, and not the other two.** A contact carries Mobile, Home and Work,
// each with an SMS Capable box (#176); only Mobile is an SMS address. Home and
// Work are stored, shown on the card, and never written to. How MMS delivers
// across the three numbers is not known.

// The audience a caller stated, or a refusal. Never a silent nobody.
//
// The key that carries a family's audience is `contacts` (#413). A caller
// writing `contact` would otherwise produce **no parents notice at all**, and
// an absent audience must not be confused with "this family has no contacts
// on file", which a teacher can create:
//
//   contacts: [a, b]   an audience
//   contacts: []       a family with nobody on file — warned about, not sent to
//   (absent)           a programming error, and this throws
//   contact: …         the single-contact spelling, and this throws naming it
//
// A throw rather than a fallback: treating an absent key as `[]` is silent,
// and treating `contact` as `[contact]` would narrow a studio's mail to one
// person. Every caller builds its group from a provider, so the throw lands
// the first time a notice surface is rendered in a test; no user input
// reaches it.
//
// `where` names the function the caller actually called, because a group built
// by `lessonCancellationRequests` is one this function only sees second-hand.
function statedAudience(where, source, family) {
  if (source && typeof source === 'object' && 'contact' in source) {
    throw new TypeError(
      `${where}: \`contact\` became \`contacts\` on #413 and takes a list — a family's `
      + 'mail goes to every contact on it, BCC\'ed into one message. Write '
      + '`contacts: [theContact]` for a one-person audience, or `contacts: []` for a '
      + 'family with nobody on file. Left as `contact` this addresses nobody, silently.',
    );
  }
  const stated = source?.contacts;
  if (Array.isArray(stated)) return stated.filter(Boolean);
  // No family, no parents notice, so there is no audience to have stated. The
  // callers that pass neither are the ones with no family in hand.
  if (!family) return [];
  throw new TypeError(
    `${where}: a group with a family must state its audience. Pass \`contacts\` — the `
    + "family's contacts in tab order (`contactsFor` in src/data/useFamilies.jsx, or "
    + '`familyContactsByFamily` in src/domain/people.js) — or `contacts: []` for a family '
    + 'with nobody on file. Absent, this would have addressed nobody without saying so.',
  );
}

// The groups as the core takes them: each group's family and id, and its
// contacts beside it keyed by position. Every group's audience is stated or
// refused first, under the name of the function the caller called. A group's
// `students` are who is on the lesson and are not sent to (#792).
function flatGroups(where, groups) {
  const out = { groups: [], contacts: [] };
  for (const [i, group] of (groups || []).entries()) {
    const family = group?.family || null;
    const contacts = statedAudience(where, group, family);
    out.groups.push({
      familyId: str(group?.familyId),
      hasFamily: Boolean(family),
      familyDocId: family ? str(family.id) : null,
      familyLastName: family ? str(family.lastName) : null,
    });
    for (const c of contacts) out.contacts.push(toContact(c, i));
  }
  return out;
}

// Audience → addresses, for one channel: the seam between "who did the
// teacher tick" and "who actually gets written to".
//
// `groups` is one entry per family the cancellation touches:
//   { familyId, family, contacts, students }  — students being that family's
//   students who are on the lesson, and nobody else's. Nobody is told as a
//   student (#792); a group with no family is told nothing.
//
// `contacts` is the family's contacts in tab order (`familyContacts` or
// `familyContactsByFamily` in ./people.js): every contact (#413). A group that
// says `contact`, or names a family and no audience, is refused — see
// `statedAudience` above. `contacts: []` lands the family in `unreachable`,
// visible in the dialog's warning.
//
// ── One notice, several recipients, and a per-contact warning (#413) ────────
//
// A family gets **one** notice, keyed `fam:<id>`, carrying every reachable
// contact in its `to` — MMS's model of one message, several BCC'ed
// recipients. One key per family stops a change of contacts from minting
// fresh message ids for lessons a studio has already told people about.
//
// **The `unreachable` half is per contact, which is ours.** A family can be in
// both halves at once — two contacts written to, a third listed as
// unreachable. How MMS's delete dialog lists a family with several contacts,
// one without an address, is not known; its captions are the plural `Email
// students` / `Email parents`.
//
// Returns both halves of the same computation, so the dialog cannot promise a
// send the page then fails to queue:
//   notices     — one per reachable recipient, carrying the id its message
//                 should be keyed on (see `key`).
//   unreachable — the contacts who have no address on this channel, and each
//                 family with nobody on file. Not an error: as in MMS, the
//                 warning lets the delete proceed.
//
// `nameFormats` is the studio's Naming Format (domain/studio.js); the warning
// lists parents, so its `parent` is read. Omitted, it is the default, "Last,
// First".
export function cancellationRecipients(
  groups = [],
  { channel = CHANNELS.email, nameFormats = undefined } = {},
) {
  const flat = flatGroups('cancellationRecipients', groups);
  const core = coreSync();
  const args = [flat.groups, flat.contacts, String(channel)];
  const format = str(nameFormats?.parent);
  const to = core.cancellationNoticeRecipients(...args);
  const notices = core.cancellationNotices(...args, format).map((n, i) => ({
    key: n.key,
    to: to.filter((r) => r.notice === i).map(fromRecipient),
    familyId: n.familyId ?? null,
    channel: n.channel,
    name: n.name ?? null,
    listName: n.listName ?? null,
  }));
  const unreachable = core.cancellationUnreachable(...args, format).map((u) => ({
    key: u.key,
    familyId: u.familyId ?? null,
    contactId: u.contactId ?? null,
    channel: u.channel,
    name: u.name ?? null,
    listName: u.listName ?? null,
  }));
  return { notices, unreachable };
}

// The queue requests a confirmed delete produces.
//
// The message id is derived from what the message is about
// (core/src/ids.rs), so:
//
//   * asking for the same cancellation twice produces the same ids, and a
//     teacher who clicks Delete twice sends one notice;
//   * two recipients on the same lesson produce different ones. A key of
//     `${occurrenceId}__${scope}` alone would be shared by two families on a
//     group lesson and one notice would overwrite the other. `key` from
//     cancellationRecipients carries who the notice is for.
//
// `chosen` is what the delete dialog hands back: for each channel, `true`
// where the family is told on it, plus the shared note.
export function cancellationRequests({ occurrence, scope, chosen = {}, groups = [] } = {}) {
  if (!occurrence) return [];
  const choice = toChoice(chosen);
  // No channel ticked asks nothing, and resolves nobody — so it refuses no
  // group either.
  if (!choice.emailOn && !choice.smsOn) return [];
  const flat = flatGroups('cancellationRecipients', groups);
  const core = coreSync();
  const args = [toOccurrence(occurrence), String(scope), choice, flat.groups, flat.contacts];
  return core.cancellationMessages(...args)
    .map((ref, i) => requestOf(ref, core.cancellationMessageDocument(...args, i), ['note']));
}

// ─── The other way a lesson is called off ───────────────────────────────────
//
// `cancellationRequests` above is the *delete* path: the lesson leaves the
// calendar and its rule date goes into the series' `exceptions`.
//
// Marking a lesson **Teacher Cancelled** in the attendance drawer calls it off
// without deleting it (#152), leaving it on the calendar with a status, and
// can send a notice too (#187). Whether MMS offers a notice on this route is
// not known (#187).
//
//   * the notice is the *same* `event_cancelled` message: "Your lesson on 15
//     September has been cancelled" is true whether the lesson left the
//     calendar or stayed on it with a status.
//   * it is **offered, never automatic**, and the ticks start off, as in the
//     delete dialog.
//
// Three things differ from the delete path, and all three are in the subject:
//
//   * `attendance` rather than a scope, so a lesson marked cancelled and *then*
//     deleted can still send its delete-path notice.
//   * the student's own id, because attendance is per student. Two siblings on
//     one group lesson, marked cancelled one after the other, must not collide
//     on one message document, as the `fam:<id>` key alone would. The notice
//     is still the family's (#792).
//   * `scope` is always `one`. An attendance record is about a single
//     occurrence; there is no "this and following" to ask about.
//
// Everything stable, so re-saving the drawer asks for the same message rather
// than a second one (see `queue` in data/useNotifications.jsx: the same id
// merges while queued and is dropped once the sender has it).
// (The subject is built in core/src/messages.rs and core/src/lesson.rs.)

// Every subject a cancellation notice for this lesson-and-student could carry,
// whichever channels the teacher ticked — the student's own `stu:` one only on
// a notice asked for before #792 — so the drawer can answer "has a notice
// already gone out?".
//
// The `fam:` key here is built from `student.familyId`; the one
// `cancellationRecipients` puts on a notice falls back to
// `familyId || family.id || ''`. They disagree for a student with no
// `familyId` and a family object present, which would miss the already-sent
// check. Unreachable from `useAttendanceForm`, which gets its family by
// `familyById(student.familyId)` (null on no match, data/useFamilies.jsx), and
// a parents notice needs a family. A caller of `lessonCancellationRequests`
// that hands in a `family` some other way must key the family the same way on
// both sides.
export function lessonCancellationSubjects({ occurrence, student } = {}) {
  if (!occurrence || !student) return [];
  return [...coreSync().lessonCancellationSubjects(
    String(occurrence.id), String(student.id), str(student.familyId),
  )];
}

// The queue requests marking one student's lesson Teacher Cancelled produces.
//
// `chosen` is the same object the delete dialog hands back — per channel,
// `true` where the family is told — so both routes speak one language to
// `cancellationRecipients`, and the addressing rules (what counts as an SMS
// address) have exactly one implementation.
//
// **Takes the options object rather than destructuring in the signature**, so
// `statedAudience` sees every key: a destructured parameter list drops
// `contact: linh` silently. Holding the object lets that spelling be refused
// under this function's own name.
export function lessonCancellationRequests(options = {}) {
  const { occurrence, student, family = null, chosen = {} } = options;
  const contacts = statedAudience('lessonCancellationRequests', options, family);
  if (!occurrence || !student) return [];
  // One family, and only this student: see the notes above. `contacts` is
  // every contact on the family (#413), resolved by `statedAudience`.
  const core = coreSync();
  const args = [
    toOccurrence(occurrence), toStudent(student), family ? toFamily(family) : null,
    contacts.map((c) => toContact(c)), toChoice(chosen),
  ];
  return core.lessonCancellationMessages(...args)
    .map((ref, i) => requestOf(ref, core.lessonCancellationMessageDocument(...args, i), ['note']));
}

// What the attendance drawer says when a reminder for this lesson has already
// been sent (#187).
//
// No reminder is *decided* for a lesson already marked Teacher Cancelled
// (#156), and `functions/delivery/facts.js` refuses one cancelled between the
// decision and the send. Once the mail has gone, nothing takes it back. This
// tells the teacher what the family is holding when they decide whether to
// send a notice; it does not tell them what to do.
export function reminderAlreadySentText(channels = []) {
  const list = (Array.isArray(channels) ? channels : [channels]).filter(Boolean).map(String);
  return coreSync().reminderAlreadySentText(list) ?? null;
}

// A queued message, normalised. `context` is ids and dates rather than
// rendered text: the sender rebuilds the body server-side from the same
// domain code the client previewed it with, so the message a family receives
// cannot disagree with what the app shows.
//
// `note` is the one exception: it is a sentence the teacher typed — MMS's "Cancellation message", the `%Note%` in
// its Event Cancelled template — which exists nowhere else to be re-derived
// from. It is stored verbatim and escaped where it meets the HTML, not here;
// see functions/delivery/templates.js.
export function queuedMessage({ channel, kind, to = [], cc = [], context = {}, note = null }) {
  // `undefined` in a context is written null: the SDK refuses `undefined`,
  // and a batch it refuses takes every message in it down.
  const defined = Object.fromEntries(
    Object.entries(context || {}).map(([k, v]) => [k, v === undefined ? null : v]),
  );
  return documentFromRows(coreSync().queuedMessageDocument(
    String(kind ?? ''),
    str(channel),
    to.filter(Boolean).map(toRecipient),
    cc.filter(Boolean).map(toRecipient),
    documentToRows(defined),
    str(note),
  ));
}

// Whether a request is worth writing at all. A message with nobody to send it
// to would show the teacher a pending item that can never be delivered.
export function isAddressed(message) {
  return Boolean(message && coreSync().isMessageAddressed(message.to.length, message.cc.length));
}

// A one-line summary of what is waiting, for the screens that say so.
export function pendingSummary(messages = []) {
  return coreSync().pendingMessageSummary(
    messages.map((m) => ({ status: str(m?.status), channel: str(m?.channel) })),
  ) ?? null;
}

// ─── Message History (#488) and Send again (#489) ─────────────────────────
//
// Which messages are a student's, their order, their words and whether a row
// offers Send again are `message_history` in core/src/messages.rs, so the web
// and both phones list the same rows. What the web adds is the instant: the
// hook hands each message over with its three instants already as epoch
// milliseconds (it can read a Timestamp; this file, copied into functions/,
// cannot import the reader), and gets `at` back as the ISO string the tab
// formats.

/**
 * The rows. `inputs` are `MessageHistoryInput`s — see
 * `src/hooks/useMessageHistory.js` for the reading of a stored message.
 */
const fromHistoryRow = (r) => ({
  ...r,
  to: [...r.to],
  error: r.error ?? null,
  at: r.at == null ? null : new Date(r.at).toISOString(),
  sendAgainTo: [...r.sendAgainTo],
  sendAgainLabel: r.sendAgainLabel ?? null,
  // MMS's From column, the teacher's words and Forward (#546).
  from: r.from ?? null,
  body: r.body ?? null,
  canForward: Boolean(r.canForward),
  // Forward's subject (#595): a teacher's own, or the one the sender kept.
  forwardSubject: r.forwardSubject ?? null,
  // Cancel Scheduled Send (#606): a message sent later, still waiting.
  canWithdraw: Boolean(r.canWithdraw),
  // The names of the files it carried (#605).
  attachments: [...(r.attachments || [])],
});

// A row as the tab holds it, back into the core's record: `at` returns to
// epoch milliseconds.
const toHistoryRow = (r) => ({
  ...r,
  at: r.at == null ? undefined : Date.parse(r.at),
  error: r.error ?? undefined,
  sendAgainLabel: r.sendAgainLabel ?? undefined,
  from: r.from ?? undefined,
  body: r.body ?? undefined,
  forwardSubject: r.forwardSubject ?? undefined,
});

export function historyRows(inputs = [], { studentId = null, familyId = null } = {}) {
  return [...coreSync().messageHistoryRows(inputs, str(studentId), str(familyId))].map(fromHistoryRow);
}

/**
 * The rows for one period (#522): the outbox's `inputs` and the delivery
 * log's `logged` rows (both `MessageHistoryInput`s, the second with
 * `logged: true`), folded by the core. `now` is epoch milliseconds.
 */
export function historyRowsInPeriod(inputs = [], logged = [], {
  studentId = null, familyId = null, period = null, now = Date.now(),
} = {}) {
  const core = coreSync();
  return [...core.messageHistoryInPeriod(
    inputs, logged, str(studentId), str(familyId), String(period ?? core.messageHistoryDefaultPeriod()), Number(now),
  )].map(fromHistoryRow);
}

/** MMS's period menu, in its order: `{ code, label, readsLog, emptyCode }`. */
export function historyPeriods() {
  return [...coreSync().messageHistoryPeriods()].map((p) => ({ ...p }));
}

/** The period the tab opens on. */
export function defaultHistoryPeriod() {
  return coreSync().messageHistoryDefaultPeriod();
}

/** The tab's words, by name. Read when drawn, not at module scope. */
export function historyWords() {
  const say = (code) => coreSync().messagingMessage(code);
  return {
    heading: say('historyHeading'),
    hint: say('historyHint'),
    empty: say('historyEmpty'),
    emptyScheduled: say('historyEmptyScheduled'),
    justNow: say('historyJustNow'),
    sendAgain: say('sendAgain'),
    from: say('historyFrom'),
    sort: say('historySort'),
    clearSort: say('historyClearSort'),
    search: say('historySearch'),
    searchPlaceholder: say('historySearchPlaceholder'),
    byType: say('historyByType'),
    searchBody: say('historySearchBody'),
    clear: say('historyClear'),
    rowMenu: say('historyRowMenu'),
    forward: say('forward'),
    scheduled: say('historyScheduled'),
    // Cancel Scheduled Send (#606) and the confirm it asks first.
    withdraw: say('historyWithdraw'),
    withdrawTitle: say('historyWithdrawTitle'),
    withdrawMessage: say('historyWithdrawMessage'),
    withdrawConfirm: say('historyWithdrawConfirm'),
    withdrawKeep: say('historyWithdrawKeep'),
  };
}

// ─── Sort and Search (#546) ────────────────────────────────────────────────
//
// MMS's Sort ▾ (Clear Sort, Date & Time, From, Status, Subject, To, Type) and
// Search ▾ (a box, By Type, "Search within the message body"). Which rows
// match and their order are the core's (`history_search`,
// `history_sorted`).

/** MMS's Sort menu after Clear Sort: `{ key, label, descending }`. */
export function historySortKeys() {
  return [...coreSync().messageHistorySortKeys()].map((k) => ({ ...k }));
}

/** MMS's By Type: `{ code, label }` for All, Email, SMS. */
export function historyTypes() {
  return [...coreSync().messageHistoryTypes()].map((t) => ({ ...t }));
}

/**
 * The rows Search keeps, in Sort's order. `rows` are `historyRowsInPeriod`'s;
 * `query` is the box (blank is everything), `byType` a `historyTypes()` code,
 * `withinBody` the checkbox, `sortKey` a `historySortKeys()` key and
 * `descending` which way.
 */
export function historyView(rows = [], {
  query = null, byType = 'all', withinBody = false, sortKey = 'date', descending = true,
} = {}) {
  const core = coreSync();
  const found = core.messageHistorySearch(rows.map(toHistoryRow), str(query), String(byType), Boolean(withinBody));
  return [...core.messageHistorySorted(found, String(sortKey), Boolean(descending))].map(fromHistoryRow);
}

// ─── Send again reaches the undelivered only (#523) ───────────────────────

const addressList = (list) => (Array.isArray(list) ? list.map((r) => String(r?.address ?? '')) : []);

const deliveredOf = (message) => {
  const list = message?.delivery?.delivered;
  return Array.isArray(list) ? list.filter((a) => typeof a === 'string') : [];
};

/**
 * Who a later round of this message goes to: the recipients on `to` and `cc`
 * with no delivered receipt in `delivery.delivered`, as the stored recipient
 * maps. The sender's (functions/delivery/deliver.js); the rule is
 * `undelivered` in core/src/messages.rs.
 */
export function undeliveredRecipients(message) {
  const core = coreSync();
  const delivered = deliveredOf(message);
  const keep = (list) => {
    const rows = Array.isArray(list) ? list : [];
    return [...core.undeliveredPositions(addressList(rows), delivered)].map((i) => rows[i]);
  };
  return { to: keep(message?.to), cc: keep(message?.cc) };
}

/** `delivered` with `address` added once — what a delivered receipt writes. */
export function withDelivered(delivered, address) {
  const list = Array.isArray(delivered) ? delivered.filter((a) => typeof a === 'string') : [];
  return [...coreSync().withDeliveredAddress(list, String(address ?? ''))];
}

// ─── New Message (#522) ────────────────────────────────────────────────────
//
// MMS's "+ New Message ▾": Email Student, Email <each family contact>, Email
// All; ours has no Email Student, a student not being a message destination
// (#792). The entries, the composer's refusals and the document are the
// core's (`compose_choices`, `compose_refusal`, `teacher_message_request`).

/** The menu's entries for a student's family: `{ key, label, to: [recipient] }`. */
export function composeChoices(family, contacts = []) {
  return [...coreSync().composeMessageChoices(
    family ? toFamily(family) : undefined, toContacts(contacts),
  )].map((c) => ({ key: c.key, label: c.label, to: [...c.to].map(fromRecipient) }));
}

/** Why the composer will not send this, as a sentence, or null. */
export function composeRefusal(to = [], subject = null, body = null) {
  const core = coreSync();
  const code = core.composeMessageRefusal(to.length, str(subject), str(body));
  return code ? core.messagingMessage(code) : null;
}

/**
 * The composer's To once Extra Recipients is read (#547): `{ to, invalid }`,
 * the people chosen then each address typed (split at a semicolon or a line
 * break, an address once), and what was typed that is not an address.
 */
export function composeRecipients(chosen = [], extra = null) {
  const r = coreSync().composeMessageRecipients(chosen.filter(Boolean).map(toRecipient), str(extra));
  return { to: [...r.to].map(fromRecipient), invalid: [...r.invalid] };
}

/** The composer's limits, for the fields' `maxLength`. */
export function composeLimits() {
  const core = coreSync();
  return { subject: core.subjectLineMax(), body: core.messageNoteMax() };
}

/** The composer's words, by name. */
export function composeWords() {
  const say = (code) => coreSync().messagingMessage(code);
  return {
    newMessage: say('newMessage'),
    to: say('composeTo'),
    subject: say('composeSubject'),
    body: say('composeBody'),
    send: say('composeSend'),
    cancel: say('composeCancel'),
    nobody: say('composeNobody'),
    extra: say('composeExtra'),
    extraHint: say('composeExtraHint'),
    extraInvalid: say('extra_invalid'),
    ccMe: say('composeCcMe'),
    forwardTitle: say('forwardTitle'),
    forwardSelect: say('forwardSelect'),
    // From, BCC and Show all (#600); Send Email (#599).
    from: say('composeFrom'),
    bcc: say('composeBcc'),
    showAll: say('composeShowAll'),
    sendEmail: say('composeSendEmail'),
    immediately: say('composeImmediately'),
    immediatelyHint: say('composeImmediatelyHint'),
    later: say('composeLater'),
    laterHint: say('composeLaterHint'),
    date: say('composeDate'),
    time: say('composeTime'),
  };
}

/**
 * The id the message New Message queues will have, from the composer's
 * `seed` — the same id `teacherMessageWrite` answers. Attach Files uploads
 * under it (#605), because the sender and the sweep find a message's files at
 * `attachments/{uid}/{this id}/`, from the message document's own id. The
 * core's (`teacher_message_id`, core/src/messages.rs), which both phones ask
 * too (#611).
 */
export const teacherMessageId = (seed) => coreSync().teacherMessageId(String(seed));

/**
 * The message New Message queues: `{ id, message, refusal }`, the message
 * with its `requestedAt` as `serverTimestamp`. `cc` is CC me's "Myself"
 * (#547, `myselfRecipient`), or nobody; `attachments` are Attach Files'
 * uploaded files (#605), each `{cap}/{name}`; `resourceIds` the old Attach
 * Files' library links (#598), which no composer sends since #611 and the
 * sender still honours on a message already queued. `seed` is a fresh id made
 * when the composer opened, so one Send is one message; `refusal` is the
 * sentence when the composer would refuse it (and then nothing should be
 * written).
 */
export function teacherMessageWrite({
  seed, to = [], cc = [], subject = null, body = null, familyId = null, studentId = null, serverTimestamp = null,
  sendAt = null, sendAs = null, resourceIds = [], studentIds = [], attachments = [],
} = {}) {
  const core = coreSync();
  // `sendAt` is Send Email's future instant in epoch milliseconds (#599),
  // null for Immediately; `sendAs` is From's key (#600), null for the
  // sender's default.
  const args = [
    String(seed), to.filter(Boolean).map(toRecipient), cc.filter(Boolean).map(toRecipient),
    str(subject), str(body), str(familyId), str(studentId),
    Number.isFinite(sendAt) ? sendAt : null, str(sendAs),
    // The old Attach Files (#598): library files, sent as links, in the
    // context as `resourceIds` (`with_attachments`). No composer sends any
    // since #611 (the web's since #605); the parameter stays for the vectors
    // that pin what the sender still honours.
    (Array.isArray(resourceIds) ? resourceIds : []).filter((id) => typeof id === 'string'),
    // The students it reached (#607, `reachedStudents`), so it is listed on
    // each one's Message History; the core bounds them (`with_students`).
    (Array.isArray(studentIds) ? studentIds : []).filter((id) => typeof id === 'string'),
    // Attach Files (#605): the uploaded files, each `{cap}/{name}`
    // (`attachmentEntry`), in the context as `attachments`.
    (Array.isArray(attachments) ? attachments : []).filter((e) => typeof e === 'string'),
  ];
  const ref = core.teacherMessage(...args);
  // `requestedAt` is the server's: the SDK's sentinel where the caller passes
  // one, null where it does not (a test).
  const message = documentFromRows(core.teacherMessageDocument(...args), serverTimestamp);
  return { id: ref.id, message, refusal: ref.refusal ? core.messagingMessage(ref.refusal) : null };
}

// ─── From, Send Email and the Select... groups (#599, #600) ───────────────
//
// MMS's composer: From is the studio's address or the teacher's own; Send
// Email is Immediately or "Automatically at a future date/time"; To's
// `Select...` adds a studio-wide group. The rules are the core's
// (`from_choices`, `send_at_refusal`, `compose_groups`).

/** From's entries, `{ key, label }`, the one the composer opens on first. */
export function composeFromChoices({
  studioName = null, studioEmail = null, teacherName = null, teacherEmail = null,
} = {}) {
  return [...coreSync().composeFromChoices(str(studioName), str(studioEmail), str(teacherName), str(teacherEmail))]
    .map((c) => ({ key: c.key, label: c.label }));
}

/** Why Send Email's instant will not do, as a sentence, or null. */
export function sendAtRefusal(sendAt, now = Date.now()) {
  const core = coreSync();
  const code = core.sendAtRefusal(Number(sendAt), Number(now));
  return code ? core.messagingMessage(code) : null;
}

/** The key of Clear Selection, the `Select...` entry that empties To. */
export function clearSelectionKey() {
  return coreSync().composeClearSelectionKey();
}

/**
 * `Select...`'s studio-wide groups, `{ key, label, to }`, Clear Selection
 * last: every student (their `status` read), every family, every contact
 * (each with its `familyId`), the teacher's name and "Show all".
 */
export function composeGroups({
  students = [], families = [], contacts = [], teacherName = null, showAll = false,
} = {}) {
  const fams = listOf(families).filter((f) => f && f.id != null);
  const at = new Map(fams.map((f, i) => [String(f.id), i]));
  const keyed = listOf(contacts)
    .filter((c) => c && at.has(String(c.familyId)))
    .map((c) => toContact(c, at.get(String(c.familyId))));
  const people = listOf(students).filter(Boolean).map((st) => ({ ...toStudent(st), status: str(st.status) }));
  return [...coreSync().composeMessageGroups(people, fams.map(toFamily), keyed, str(teacherName), Boolean(showAll))]
    .map((c) => ({ key: c.key, label: c.label, to: [...c.to].map(fromRecipient) }));
}

/**
 * Whose Message History a message to `to` is listed on (#607): the ids of
 * every student of a family one of whose contacts' address is on it
 * (`reached_students` in core/src/messages.rs, where the reasoning is). The
 * studio's lists as `composeGroups` takes them.
 */
export function reachedStudents({
  to = [], students = [], families = [], contacts = [],
} = {}) {
  const fams = listOf(families).filter((f) => f && f.id != null);
  const at = new Map(fams.map((f, i) => [String(f.id), i]));
  const keyed = listOf(contacts)
    .filter((c) => c && at.has(String(c.familyId)))
    .map((c) => toContact(c, at.get(String(c.familyId))));
  const people = listOf(students).filter(Boolean).map((st) => toStudent(st));
  return [...coreSync().composeReachedStudents(
    listOf(to).filter(Boolean).map(toRecipient), people, fams.map(toFamily), keyed,
  )];
}

/**
 * Send again's write, merged onto the stored message: back to `queued` with
 * nothing else changed, which is what the rules hold an ask to.
 * `serverTimestamp` is the SDK's sentinel for `requestedAt`.
 */
export function sendAgainDocument(serverTimestamp) {
  return documentFromRows(coreSync().sendAgainMessageDocument(), serverTimestamp);
}

/** How many times one message may be asked for again — the sender's bound too. */
export function sendAgainMax() {
  return coreSync().sendAgainMax();
}

/** What the sender writes on an ask past `sendAgainMax()`. */
export function sendAgainRefusedText() {
  return coreSync().messagingMessage('sendAgainRefused');
}

// ─── The surfaces' decisions ────────────────────────────────────────────────
//
// What the three surfaces that queue a message decide — the drawer that
// records a payment, the attendance drawer and the delete dialog — and what
// `queue` in src/data/useNotifications.jsx folds before it writes.

// The outbox's fold: which requests become which documents. A request whose
// message is addressed to nobody is dropped, and so is one whose id the sender
// has already taken (`sending` or `sent`) — see `queue` for why a repeat is
// dropped here rather than attempted. `failed` is not taken: asking again is
// the only retry a teacher has.
//
// Each write is `{ id, message }`, in request order; the provider adds the
// server's `requestedAt` and merges, so re-asking re-addresses.
export function messageWrites(requests = [], stored = []) {
  const core = coreSync();
  const messages = requests.map((request) => queuedMessage(request));
  // `message.channel` rather than the request's own: `queuedMessage` folds
  // an unrecognised channel to email, and the id is of what is stored —
  // which `messageRequestId` derives the same way.
  const refs = requests.map((request, i) => ({
    id: core.messageRequestId(String(request.kind ?? ''), str(request.channel), String(request.subject)),
    kind: messages[i].kind,
    channel: messages[i].channel,
    subject: String(request.subject),
    addressed: isAddressed(messages[i]),
    // A message the rules would refuse is left out rather than failing every
    // other message in the batch with it.
    refusal: core.messageRefusal(
      (messages[i].to || []).length, (messages[i].cc || []).length, str(messages[i].note),
    ) ?? undefined,
  }));
  const rows = stored.map((m) => ({ id: String(m.id), status: str(m.status) }));
  return [...core.messagesToWrite(refs, rows)].map((i) => ({ id: refs[i].id, message: messages[i] }));
}

// The teacher's own copy on the attendance drawer's messages: "Myself", at the
// address they signed in with.
export const MYSELF = 'Myself';
export function myselfRecipient(email) {
  return fromOptional(coreSync().myselfRecipient(str(email)));
}

// The receipt Add Transaction queues for a payment it has just recorded —
// every contact on the family (#413), keyed on the entry's own id, so
// recording the same payment twice asks for one receipt. Whether it is asked
// for at all is `receiptRequested` in ./familyAccount.js.
export function paymentReceiptRequest({ family, contacts, familyId, entryId, occurredOn } = {}) {
  const core = coreSync();
  const args = [
    family ? toFamily(family) : null, toContacts(contacts), str(familyId), String(entryId ?? ''), str(occurredOn),
  ];
  const [ref] = core.paymentReceiptMessages(...args);
  return requestOf(ref, core.paymentReceiptDocument(...args));
}

// The messages the attendance drawer can ask for, in the order it would queue
// them: the receipt for a payment taken at the lesson, the lesson notes, and —
// when the lesson is being called off and nobody has been told yet — the
// cancellation notices (#187).
//
// **Save asks for none of them (#732)**; each has its own send button,
// which asks for its one kind with `only` (`ATTENDANCE_SENDS` below). Where
// that differs from MMS is at `attendance_send_state` in core/src/messages.rs.
//
//   contacts        the family's `familyContacts` records, tab order
//   myEmail         the teacher's own address, for "Myself"
//   receiptTo       the address picked in the receipt select, or ''
//   receiptCcMe     the "copy me" tick
//   notesTo         the addresses ticked for the lesson notes
//   paymentApplies  whether a payment is being recorded at all
//   showCancellationNotices, chosen   the notice picker, as the drawer holds it
//   only            one kind of message, for a send button; absent for all
function attendanceArgs(options) {
  const {
    occurrence, student, family = null, contacts: familyContacts = [], myEmail = null,
    receiptTo = '', receiptCcMe = false, notesTo = [], paymentApplies = false,
    showCancellationNotices = false, chosen = {}, only = null,
  } = options;
  return [
    {
      myEmail: str(myEmail),
      receiptTo: str(receiptTo),
      receiptCcMe: Boolean(receiptCcMe),
      paymentApplies: Boolean(paymentApplies),
      showCancellationNotices: Boolean(showCancellationNotices),
      only: str(only),
    },
    toOccurrence(occurrence),
    toStudent(student),
    family ? toFamily(family) : null,
    toContacts(familyContacts),
    (notesTo || []).map(String),
    toChoice(chosen),
  ];
}

export function attendanceMessageRequests(options = {}) {
  if (!options.occurrence || !options.student) return [];
  const core = coreSync();
  const args = attendanceArgs(options);
  return core.attendanceMessages(...args).map((ref, i) => requestOf(
    ref,
    core.attendanceMessageDocument(...args, i),
    ref.kind === MESSAGE_KINDS.payment_receipt ? ['cc'] : ref.kind === MESSAGE_KINDS.event_cancelled ? ['note'] : [],
  ));
}

// The attendance drawer's three send buttons (#732): which kind each one
// sends, and its label. The labels are the core's (`message` in
// core/src/messages.rs), so the phones' buttons read the same.
export const ATTENDANCE_SENDS = Object.freeze({
  notes: MESSAGE_KINDS.lesson_notes,
  receipt: MESSAGE_KINDS.payment_receipt,
  notice: MESSAGE_KINDS.event_cancelled,
});

export const ATTENDANCE_SEND_STATES = Object.freeze({
  unavailable: 'unavailable', ready: 'ready', sent: 'sent',
});

export function attendanceSendWording() {
  const say = (code) => coreSync().messagingMessage(code);
  return {
    notes: say('attendanceSendNotes'),
    receipt: say('attendanceSendReceipt'),
    notice: say('attendanceSendNotice'),
    sent: say('attendanceSent'),
    sentLine: say('attendanceSentLine'),
    alreadySent: say('attendanceAlreadySent'),
    noticeQueued: say('attendanceNoticeQueued'),
  };
}

// What one send button offers — `unavailable`, `ready` or `sent` — for the
// drawer's state in `options` (as `attendanceMessageRequests` takes it, with
// `only` naming the button's kind) against the outbox as it stands. The rule
// for each button is `attendance_send_state` in core/src/messages.rs.
//
//   notes       the drawer's three notes; the student and parent ones are
//               what an email carries
//   paidMinor   what Paid at Lesson reads, in minor units
export function attendanceSendState(options = {}, stored = [], { notes = {}, paidMinor = 0 } = {}) {
  if (!options.occurrence || !options.student || !options.only) return ATTENDANCE_SEND_STATES.unavailable;
  const rows = (stored || []).map((m) => ({ id: String(m.id), status: str(m.status) }));
  return coreSync().attendanceSendState(
    ...attendanceArgs(options), rows, str(notes.student), str(notes.parent), Number(paidMinor) || 0,
  );
}

// The delete dialog's groups: one per family on the lesson, in the order its
// students first appear, holding that family's students only. Students with
// no family are one group of their own, with no family and so told nothing. A
// hole in the list is not a student.
// src/pages/CalendarPage.jsx hands in its providers' two lookups.
export function occurrenceNoticeGroups(students = [], { familyById, contactsFor } = {}) {
  const present = students.filter(Boolean);
  const members = coreSync().occurrenceNoticeGroups(
    present.map((st) => (st.familyId ? String(st.familyId) : '')),
  );
  const groups = [];
  for (const m of members) {
    const student = present[m.student];
    if (!groups[m.group]) {
      const familyId = m.familyId || null;
      groups[m.group] = {
        familyId,
        family: familyId ? familyById(familyId) : null,
        contacts: familyId ? contactsFor(familyId) : [],
        students: [],
      };
    }
    groups[m.group].students.push(student);
  }
  return groups;
}

// The attendance drawer's two notice channels. MMS's delete-dialog wording
// with the warning's tail rewritten for a save rather than a delete; it still
// says nothing is blocked. The delete dialog's own is `NOTICE_WORDING` in
// ./calendarWording.js.
export const ATTENDANCE_NOTICE_WORDING = Object.freeze({
  email: Object.freeze({
    label: 'Send email cancellation notices',
    warning: 'The following recipients have no email address on file. They will not receive notifications, but you can still save this lesson.',
  }),
  sms: Object.freeze({
    label: 'Send SMS cancellation notices',
    warning: 'The following recipients have no SMS capable phone number on file. They will not receive notifications, but you can still save this lesson.',
  }),
});

// The one line under every disabled "send a text" control while this
// deployment cannot carry one (#782, ./smsGate.js).
export const SMS_UNAVAILABLE_TEXT = "Text messages aren't available yet.";

// Whether one channel's resolved notices would write anything: a channel
// ticked with nobody reachable behind it queues nothing, and a line about it
// would be noise.
export function willSend(resolved) {
  return (resolved?.notices?.length || 0) > 0;
}
